import { HttpClient, HttpContext, HttpHeaders, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { ResponseDto } from "../../models/generic/response.dto";
import { JSONHelper } from "../../shared/helpers/json.helper";
import { SkipLoading } from "../../interceptors/loading.interceptor";

@Injectable({
    providedIn: 'root'
})
export class RestService {
    constructor(protected httpClient: HttpClient){ }

/************************************************************************************************************************************************************************ */
/**                                                                GET                                                                                                    */
/************************************************************************************************************************************************************************ */


    /**
     * Función GET para la consulta REST con token de acceso
     * @param url Parametro que recibe la url del servicio REST
     * @param notLoading Parámetro opcional que es para interceptar o no la comunicacion con el backend y mostrar el loading.
     * @returns Retorna un observable con el tipo de dato especificado T
     */
    get<T>(url : string, notLoading?: boolean){
        if (notLoading) {
            return this.httpClient.get<T>(url, { context: this.skipLoading() });
        } else {
            return this.httpClient.get<T>(url);
        }
    }

    /**
     * Función GET que recibe parametros para consultar informacion, requiere el token de acceso en el encabezado
     * @param url Parametro que recibe la url del servicio GET
     * @param params Parametro que recibe un objeto tipo Json con los datos para crear un objeto HttpParams
     * @param notLoading Parámetro opcional que es para interceptar o no la comunicacion con el backend y mostrar el loading.
     * @returns Retorna un observabel con el listado de datos solicitados
     */
    getWithParam<T>(url : string, params: Map<string, any>, notLoading?: boolean){
        if (notLoading) {
            return this.httpClient.get<T>(url, { params: this.createParamsGet(params), context: this.skipLoading() });
        } else {
            return this.httpClient.get<T>(url, { params: this.createParamsGet(params) });
        }
    }

    /**
     * Función GET para la consulta REST con token de acceso
     * @param url Parametro que recibe la url del servicio REST
     * @returns Retorna un observable con el tipo de dato especificado T
     */
    getWithTokenFileDownload(url : string){
        return this.httpClient.get(url, { observe: 'response', responseType: 'blob'  });
    }


/************************************************************************************************************************************************************************ */
/**                                                                POST                                                                                                    */
/************************************************************************************************************************************************************************ */

    
    /**
     * Función POST para enviar un file a los servicios REST usando autenticación
     * @param url Parametro que recibe la url del servicio REST
     * @param object Objeto que se enviará al servicio REST
     * @returns Retorna el response con el estatus del envío
     */
    post<T>(url : string, object : any, notLoading?: boolean){
        if (notLoading) {
            return this.httpClient.post<T>(url, object, { context: this.skipLoading() });
        } else {
            return this.httpClient.post<T>(url, object);
        }
    }

    /**
     * Función POST para enviar información a los servicios REST usando autenticación y obteniendo el Response para extraer info del header
     * @param url Parametro que recibe la url del servicio REST
     * @param object Objeto que se enviará al servicio REST
     * @param notLoading Parámetro opcional que es para interceptar o no la comunicacion con el backend y mostrar el loading.
     * @returns Retorna el response con el estatus del envío
     */
    postWithResponse<T>(url : string, object : any, notLoading?: boolean){
        if (notLoading) {
            return this.httpClient.post<T>(url, object, { observe: 'response', context: this.skipLoading() });
        } else {
            return this.httpClient.post<T>(url, object, { observe: 'response' });
        }
    }

    /**
     * Función POST para enviar un file a los servicios REST usando autenticación
     * @param url Parametro que recibe la url del servicio REST
     * @param object Objeto que se enviará al servicio REST
     * @returns Retorna el response con el estatus del envío
     */
    postWithTokenFile<T>(url : string, object : any){
        return this.httpClient.post<T>(url, object, {headers: this.getHttpHeaderFile() });
    }



/************************************************************************************************************************************************************************ */
/**                                                                PUT                                                                                                    */
/************************************************************************************************************************************************************************ */

    
    /**
     * Funcion PUT para enviar información en el body al servicio REST utilizando autenticación
     * @param url Parametro que recibe la url del servicio REST
     * @param object Objeto de datos que será enviado para una actualizacion
     * @param notLoading Parámetro opcional que es para interceptar o no la comunicacion con el backend y mostrar el loading.
     * @returns Retorna un observable
     */
    put<T>(url : string, object : any, notLoading?: boolean){
        if (notLoading) {
            return this.httpClient.put<T>(url, object, { context: this.skipLoading() });
        } else {
            return this.httpClient.put<T>(url, object);
        }
    }




/************************************************************************************************************************************************************************ */
/**                                                                DELETE                                                                                                 */
/************************************************************************************************************************************************************************ */

    /**
     * Función DELETE para hacer la eliminación de registros usando el servicio REST con autenticación
     * @param url Es el parametro que contiene la url del endpoint del servicio REST
     * @param notLoading Parámetro opcional que es para interceptar o no la comunicacion con el backend y mostrar el loading.
     * @returns Retorna un observable
     */
    delete<T>(url : string, notLoading?: boolean){
        if (notLoading) {
            return this.httpClient.delete<T>(url, { context: this.skipLoading() });
        } else {
            return this.httpClient.delete<T>(url);
        }
    }



/************************************************************************************************************************************************************************ */
/**                                                                ENCABEZADO, PARAMETROS Y RESPONSE                                                                      */
/************************************************************************************************************************************************************************ */

    /**
     * Encabezado para archivos en servicios REST
     * @returns 
     */
    private getHttpHeaderFile(){
        return new HttpHeaders(
            {
                'Content-type':'multipart/form-data',
                'enctype':'multipart/form-data'
            }
        );
    }

    
    /**
     * Función privada e interna que permtie crear los parametros del metodo GET.
     * @param listParam Listado tipo map para crear los parametros en el objeto HttpParams
     * @returns Retorna un objeto HttpParams con los parametros agregados
     */
    private createParamsGet(listParam: Map<string, any>){
        let params = new HttpParams();
        for (const [key, value] of listParam) {
            params = params.set(key, value);
        }
        return params;
    }

    /**
     *
     * @param res Respuesta del servicio para extraer los datos
     * @param key Es la llave con el cual esta nombrado donde se extraeran los datos, cuando se omite se toma directamente del nombre: response o information
     * @param isList Indica si el resultado será una lista como respuesta de retorno, el cual debe ser TRUE, caso contrario se toma como un objeto {}
     * @returns Retorna un objeto {} o una lista [], de acuerdo al parametro isList
     */
    getResultResponse(res: ResponseDto, key: string, isList: boolean): any{
        let obj = isList ? [] : {};
        if (res.codeStatus && res.codeStatus.toString() === '200') {
            if (key !== "") {
                if (res.information) {
                    obj = JSONHelper.getDataByKey(res.information, key);
                } else {
                    obj = JSONHelper.getDataByKey(res.response, key);
                }
            } else {
                if (res.information) {
                    obj = res.information;
                } else {
                    obj = res.response;
                }
            }
        } else if(key !== '') {
            obj = JSONHelper.getDataByKey(res, key);
        } else {
            obj = res;
        }
        return obj;
    }

    /**
     * Función para activar o desactivar un token en el contexto, para saber si se activa o desactiva el loading.
     * @returns Retorna una opcion con valor 'true' al Http context.
     */
    skipLoading(){
        return new HttpContext().set(SkipLoading, true);
    }

}
